import type { FilterSortOption } from '$global/features/filter-drawer/types';

export enum EditionsGallerySort {
	Newest = '-release_date',
	Oldest = 'release_date',
	ArtistAsc = 'artists.Artists_id.name',
	ArtistDsc = '-artists.Artists_id.name',
	TitleAsc = 'name',
	TitleDsc = '-name',
	NumberAsc = 'edition_code_number',
	NumberDsc = '-edition_code_number',
	LetterAsc = 'edition_code_letter',
	LetterDsc = '-edition_code_letter',
	SeriesNumberAsc = 'series.series_code_number',
	SeriesNumberDsc = '-series.series_code_number',
	// Sub-series stand-ins (The Beautiful Paintings H12-1 … H12-8) tie on
	// every key above: one release date, edition number 1, no letter, and no
	// series code number except on H12-1. Series ids follow creation order, so
	// they order the sub-series by number the way edition numbers order a
	// series' editions: highest first under Newest, lowest first under Oldest.
	SeriesIdAsc = 'series.id',
	SeriesIdDsc = '-series.id',
	// Total-order tiebreaker (see SeriesGallerySort.CodeAsc): `code` is unique,
	// so Postgres can never fall back to an arbitrary order that shifts
	// between pages.
	CodeAsc = 'code',
	CodeDsc = '-code',
}

export enum SeriesGallerySort {
	NumberAsc = 'edition_code_number',
	NumberDsc = '-edition_code_number',
	LetterAsc = 'edition_code_letter',
	LetterDsc = '-edition_code_letter',
	// Number and letter repeat across a series' sub-series, so on their own they
	// leave large groups of tied rows. Postgres orders ties arbitrarily, and the
	// order it picks varies with the limit/offset window, so a paginated gallery
	// returns some editions on two consecutive pages and skips others entirely.
	// `code` is unique per edition and makes the ordering total.
	CodeAsc = 'code',
	CodeDsc = '-code',
}

export const editionsGallerySort: FilterSortOption[] = [
	{ value: EditionsGallerySort.Newest, dataCy: 'newest', label: 'Newest' },
	{ value: EditionsGallerySort.Oldest, dataCy: 'oldest', label: 'Oldest' },
	{
		value: EditionsGallerySort.ArtistAsc,
		dataCy: 'artistAsc',
		label: 'Artist Asc',
	},
	{
		value: EditionsGallerySort.ArtistDsc,
		dataCy: 'artistDsc',
		label: 'Artist Desc',
	},
];

export const seriesGallerySort: FilterSortOption[] = [
	{
		value: SeriesGallerySort.NumberAsc,
		dataCy: 'number-asc',
		label: 'Number Asc',
	},
	{
		value: SeriesGallerySort.NumberDsc,
		dataCy: 'number-desc',
		label: 'Number Desc',
	},
];

// Tiebreakers behind the Newest sort. The series number runs descending here
// (Postgres puts nulls first in descending order) so that a sub-series whose
// siblings have no series code number — H12-1 — lands last, matching the
// series-id order below. For two series released on the same day this also
// puts the higher-numbered (newer) series first, in keeping with "Newest".
export const EDITIONS_GALLERY_DEFAULT_SORTS_ASC = [
	EditionsGallerySort.SeriesNumberDsc,
	EditionsGallerySort.NumberDsc,
	EditionsGallerySort.LetterAsc,
	EditionsGallerySort.SeriesIdDsc,
	EditionsGallerySort.CodeDsc,
];

export const EDITIONS_GALLERY_DEFAULT_SORTS_ARTIST = [
	EditionsGallerySort.Newest,
	...EDITIONS_GALLERY_DEFAULT_SORTS_ASC,
];

// Tiebreakers behind the Oldest sort: the mirror image of the Newest ones.
export const EDITIONS_GALLERY_DEFAULT_SORTS_DESC = [
	EditionsGallerySort.SeriesNumberAsc,
	EditionsGallerySort.NumberAsc,
	EditionsGallerySort.LetterAsc,
	EditionsGallerySort.SeriesIdAsc,
	EditionsGallerySort.CodeAsc,
];
